import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { resetDeliveryAddressConfirmation } from '@/api/orders'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import type { Order } from '@/types/order'
import { formatMoscowDateTime } from '@/utils/format'

/**
 * Блок «Адрес доставки» в карточке заказа (админка).
 *
 * Не редактирует адрес: подтверждение делает заказчик в мобильном приложении.
 * Админ/диспетчер могут сбросить подтверждение через кнопку «Сбросить»
 * (вызывает RPC `reset_delivery_address_confirmation`) — это позволяет
 * заказчику переподтвердить адрес, если он ошибся, пока инспектор не выехал.
 *
 * Состояния (по {@link Order.delivery_address_confirmed_at},
 * {@link Order.transportation_started_at}, {@link Order.delivery_address}):
 *
 *  - confirmed_at = null, address = null  → «Заказчик ещё не указал адрес»
 *  - confirmed_at = null, address ≠ null  → «Адрес введён, ждём подтверждения»
 *  - confirmed_at ≠ null, started_at = null → «Подтверждён в HH:MM» + кнопка
 *    «Сбросить подтверждение»
 *  - started_at ≠ null → read-only «Зафиксирован: инспектор выехал в HH:MM»
 *
 * Кнопка сброса видна только если `canReset = true` (передаётся снаружи
 * на основе роли — admin/dispatcher).
 */
export function DeliveryAddressBlock({
  order,
  canReset,
}: {
  order: Order
  canReset: boolean
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const mutReset = useMutation({
    mutationFn: () => resetDeliveryAddressConfirmation(order.id),
    onSuccess: () => {
      setConfirmOpen(false)
      qc.invalidateQueries({ queryKey: ['order', order.id] })
    },
  })

  const confirmedAt = order.delivery_address_confirmed_at ?? null
  const startedAt = order.transportation_started_at ?? null
  const address = (order.delivery_address ?? '').trim()
  const inTransit = Boolean(startedAt)
  const confirmed = Boolean(confirmedAt)

  let statusLabel: string
  let statusColor: string
  if (inTransit) {
    statusLabel = startedAt
      ? t('orderBlocks.delivery.fixedAt', { time: formatMoscowDateTime(startedAt) })
      : t('orderBlocks.delivery.fixed')
    statusColor = 'var(--stryzh-color-text-secondary)'
  } else if (confirmed) {
    statusLabel = confirmedAt
      ? t('orderBlocks.delivery.confirmedAt', { time: formatMoscowDateTime(confirmedAt) })
      : t('orderBlocks.delivery.confirmed')
    statusColor = 'var(--stryzh-color-accent-green, #22c55e)'
  } else if (address) {
    statusLabel = t('orderBlocks.delivery.waitingConfirm')
    statusColor = 'var(--stryzh-color-accent-orange)'
  } else {
    statusLabel = t('orderBlocks.delivery.noAddress')
    statusColor = 'var(--stryzh-color-text-secondary)'
  }

  return (
    <section className="stryzh-card">
      <h2 className="stryzh-heading-card mb-3">{t('orderBlocks.delivery.title')}</h2>
      <dl className="grid gap-3">
        <div>
          <dt className="stryzh-input-label">{t('orderBlocks.delivery.addressLabel')}</dt>
          <dd className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-base)] text-[color:var(--stryzh-color-text-primary)]">
            {address || '—'}
          </dd>
        </div>
        <div>
          <dt className="stryzh-input-label">{t('orderBlocks.delivery.statusLabel')}</dt>
          <dd
            className="text-sm"
            style={{ color: statusColor }}
            aria-live="polite"
          >
            {statusLabel}
          </dd>
        </div>
        {confirmed && !inTransit && canReset ? (
          <div>
            <button
              type="button"
              className="stryzh-btn-ghost"
              onClick={() => setConfirmOpen(true)}
              disabled={mutReset.isPending}
            >
              {t('orderBlocks.delivery.resetBtn')}
            </button>
            {mutReset.error ? (
              <div className="mt-1 text-sm text-[color:var(--stryzh-color-accent-red,#ef4444)]">
                {(mutReset.error as Error).message}
              </div>
            ) : null}
          </div>
        ) : null}
      </dl>

      <ConfirmDialog
        open={confirmOpen}
        title={t('orderBlocks.delivery.confirmTitle')}
        confirmLabel={mutReset.isPending ? t('orderBlocks.delivery.resetting') : t('orderBlocks.delivery.reset')}
        cancelLabel={t('orderBlocks.delivery.cancel')}
        confirmVariant="danger"
        onConfirm={() => mutReset.mutate()}
        onCancel={() => setConfirmOpen(false)}
      >
        {t('orderBlocks.delivery.confirmBody')}
      </ConfirmDialog>
    </section>
  )
}
